import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import * as OrganizationsActions from '@secure/organizations/store/actions/organizations.actions';
import { DEVICE_STATE, INTERFACE_STATUS } from '@shared/constants/constants';
import { Permissions } from '@shared/constants/permission.constant';
import { NavigationService } from '@shared/services/custom-navigation.service';
import { GlobalLanguageService } from '@shared/services/global-language.service';
import { IntervalService } from '@shared/services/interval.service';
import { SharedService } from '@shared/services/shared.service';
import { StoreState } from '@views/secure/devices/store/models/devices.model';
import * as L from 'leaflet';
import 'leaflet.markercluster';
import { isUndefined, orderBy, sortBy } from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { OrganizationsService } from '../services/organizations.service';
import {
  Device,
  DeviceListType,
  OrganizationDetail,
  TunnelDetails,
} from '../store/models/organizations.model';
import {
  SetActiveDynamicRoutingSubService,
  SetActiveNetSubService,
  SetActiveTsSettingsSubService,
  SetActiveTsSubService,
  SetActiveVPNSubService,
  SetActiveSuricataSubService,
  SetLocallyChangedField,
  SetVersionChanged,
  SetVpnServerChangedFlag,
  SetActiveRoutingSubService,
  SetActiveDnsSubService,
} from '@views/secure/devices/store/actions/device-configuration.action';
import { ClearCurrentBridgeInterfaceConfiguration } from '@views/secure/devices/store/actions/bridge-interface-configuration.action';
import { ClearDnat } from '@views/secure/devices/store/actions/dnat-configuration.action';
import { ResetDynamicDnsConfiguration } from '@views/secure/devices/store/actions/dynamic-dns.action';
import { ClearCurrentHardwareInterfaceConfiguration } from '@views/secure/devices/store/actions/hardware-interface-configuration.action';
import { ClearCurrentIpsecInterface } from '@views/secure/devices/store/actions/ipsec-interface.action';
import { ClearCurrentPrismaInterface } from '@views/secure/devices/store/actions/prisma-interface.action';
import { ClearCurrentQoS } from '@views/secure/devices/store/actions/qos-configuration.action';
import { ClearRemoteSSH } from '@views/secure/devices/store/actions/remote-ssh-configuration.action';
import { ClearSplitDns } from '@views/secure/devices/store/actions/split-dns-configuration.action';
import { ClearStaticRouting } from '@views/secure/devices/store/actions/static-routing-configuration.action';
import { ClearStaticRoutingDetail } from '@views/secure/devices/store/actions/static-routing-detail-configuration.action';
import { ClearCurrentDevice } from '@views/secure/devices/store/actions/traffic-steering-configuration.action';
import { ClearTrafficSteeringStatistics } from '@views/secure/devices/store/actions/traffic-steering-statistics.action';
import { ClearCurrentTunnelInterfaceConfiguration } from '@views/secure/devices/store/actions/tunnel-interface-configuration.action';
import { ClearCurrentUsbInterfaceConfiguration } from '@views/secure/devices/store/actions/usb-interface-configuration.action';
import { ClearCurrentVlanInterfaceConfiguration } from '@views/secure/devices/store/actions/vlan-interface-configuration.action';
import { ClearVpnRouting } from '@views/secure/devices/store/actions/vpn-routing-configuration.action';
import { ClearVpnRoutingServer } from '@views/secure/devices/store/actions/vpn-routing-server-configuration.action';
import { ClearCurrentWirelessInterfaceConfiguration } from '@views/secure/devices/store/actions/wireless-interface-configuration.action';
import { ClearStaticHostConfiguration } from '@views/secure/devices/store/actions/static-hosts.action';
import { clearIpsecServerAction } from '@views/secure/devices/configuration/ipsec/server/store/ipsec-server.action';
import { clearIpsecClientAction } from '@views/secure/devices/configuration/ipsec/client/store/ipsec-client.action';
import { clearIpsecCertificateAction } from '@views/secure/devices/configuration/ipsec/certificate/store/ipsec-certificate.action';
import { clearIpsecStatisticsAction } from '@views/secure/devices/configuration/ipsec/ipsec-statistics/store/ipsec-statistics.action';
import { clearActiveHosts } from '@views/secure/devices/store/actions/active-hosts.action';
import { clearTrafficStatistics } from '@views/secure/devices/configuration/networking-interface/traffic-statistics/store/traffic-statistics.action';
import { clearUserGroups } from '@views/secure/devices/configuration/traffic-steering/user-groups/store/user-groups.action';
import { clearTsForwardSettings } from '@views/secure/devices/configuration/traffic-steering/ts-forward-settings/store/ts-forward-settings.action';
import {
  clearCellularInterface,
  updateLastRefreshedAt,
} from '@views/secure/devices/configuration/networking-interface/cellular-interface/store/cellular-interface.action';
import { clearSuricataConfiguration } from '@views/secure/devices/configuration/suricata/store/suricata.action';
import { clearBypassList } from '@views/secure/devices/configuration/traffic-steering/bypass-list/store/bypass-list.action';
import {
  ClearDynamicRoutingConfiguration,
  ClearExistingTunnelConfiguration,
} from '@views/secure/devices/configuration/routing/dynamic-routing/store/dynamic-routing.action';
import { clearDynamicRoutingStatisticsAction } from '@views/secure/devices/configuration/routing/dynamic-routing/statistics/store/dynamic-routing-statistics.action';
import { clearPolicyRoutingStatisticsAction } from '@views/secure/devices/configuration/routing/policy-routing-statistics/store/policy-routing-statistics.action';

const FETCH_INTERVAL_DURATION = 10000;

@Component({
  templateUrl: './detail.component.html',
  styleUrls: ['./detail.component.scss'],
  providers: [IntervalService],
})
export class DetailComponent implements OnInit, AfterViewInit, OnDestroy {
  isPageLoading = true;
  organization: OrganizationDetail;
  devices: Device[];
  devicesList: Device[];
  activeDevices: Device[] = [];
  offlineDevices: Device[] = [];
  notProvisionedDevices: Device[] = [];
  wanUpDevices: Device[] = [];
  wanDownDevices: Device[] = [];
  tunnelUpDevices: Device[] = [];
  tunnelDownDevices: Device[] = [];
  deviceListTypes = DeviceListType;
  activeDeviceList = this.deviceListTypes.deviceList;
  isTableView: boolean;
  canManageOrganization = Permissions.manageOrganization;
  lastSynced = new Date();
  initialRendering: boolean = true;
  gridApi: any;
  columnApi: any;
  private map: L.Map;
  private markerLayersClusterGroup: L.MarkerClusterGroup = L.markerClusterGroup();
  private id: number;
  private subscriptions = new Subscription();
  isCollapsed = true;
  showBack = true;
  currentPlotDeviceMarkersType;
  markerClusterClicked = false;
  isSuperAdmin: boolean;
  deviceColumnDefs = [
    { field: 'location', headerName: this.localizeHeader('DEVICES.LIST.LOCATION') },
    { field: 'name', headerName: this.localizeHeader('DEVICES.LIST.NAME') },
    { field: 'uuid', headerName: this.localizeHeader('DEVICES.LIST.UUID') },
    {
      field: 'is_online',
      headerName: this.localizeHeader('DEVICES.LIST.WEBSOCKET'),
      valueGetter: (params) => (params.data.is_online ? 'Online' : 'Offline'),
    },
    { field: 'type', headerName: this.localizeHeader('DEVICES.LIST.TYPE') },
    { field: 'state', headerName: this.localizeHeader('DEVICES.LIST.STATE') },
  ];

  wanColumnDefs = [
    {
      field: 'device_name',
      headerName: this.localizeHeader('DEVICES.WANLIST.DEVICE_NAME'),
      resizable: true,
    },
    {
      field: 'user_defined_name',
      headerName: this.localizeHeader('DEVICES.WANLIST.USER_DEFINED_NAME'),
      resizable: true,
    },
    {
      field: 'interface_ip',
      headerName: this.localizeHeader('DEVICES.WANLIST.INTERFACE_IP'),
      resizable: true,
    },
    {
      field: 'isp_name',
      headerName: this.localizeHeader('DEVICES.WANLIST.ISP_NAME'),
      resizable: true,
    },
    {
      field: 'isp_link_speed_in_mbps',
      headerName: this.localizeHeader('DEVICES.WANLIST.ISP_LINK_SPEED'),
      resizable: true,
    },
    {
      field: 'latency',
      headerName: this.localizeHeader('DEVICES.WANLIST.LATENCY'),
      width: 80,
      resizable: true,
    },
    {
      field: 'tx_throughput_in_bytes',
      headerName: this.localizeHeader('DEVICES.WANLIST.TX_THROUGHPUT'),
      resizable: true,
    },
    {
      field: 'rx_throughput_in_bytes',
      headerName: this.localizeHeader('DEVICES.WANLIST.RX_THROUGHPUT'),
      resizable: true,
    },

    {
      field: 'tx_utilization_in_percentage',
      headerName: this.localizeHeader('DEVICES.WANLIST.TX_UTILIZATION'),
      resizable: true,
    },
    {
      field: 'rx_utilization_in_percentage',
      headerName: this.localizeHeader('DEVICES.WANLIST.RX_UTILIZATION'),
      resizable: true,
    },
  ];

  tunnelColumnDefs = [
    {
      field: 'device_name',
      headerName: this.localizeHeader('DEVICES.TUNNEL_METRICS_LIST.DEVICE_NAME'),
      resizable: true,
    },
    {
      field: 'user_defined_name',
      headerName: this.localizeHeader('DEVICES.TUNNEL_METRICS_LIST.INTERFACE'),
      resizable: true,
    },
    {
      field: 'interface_state',
      headerName: this.localizeHeader('DEVICES.TUNNEL_METRICS_LIST.INTERFACE_STATE'),
      resizable: true,
    },
    {
      field: 'interface_ip',
      headerName: this.localizeHeader('DEVICES.TUNNEL_METRICS_LIST.INTERFACE_IP'),
      resizable: true,
    },
  ];

  columnDefs = this.deviceColumnDefs;

  private readonly onlineIcon = L.icon({
    iconUrl: 'assets/img/icons/location-green.png',
    iconSize: [30, 30],
    iconAnchor: [12, 41],
    tooltipAnchor: [16, -28],
  });

  private readonly offlineIcon = L.icon({
    iconUrl: 'assets/img/icons/location-red.png',
    iconSize: [30, 30],
    iconAnchor: [12, 41],
    tooltipAnchor: [16, -28],
  });

  constructor(
    private navigationService: NavigationService,
    private router: Router,
    private route: ActivatedRoute,
    private organizationService: OrganizationsService,
    private _intervalService: IntervalService,
    public toast: ToastrService,
    public store: Store<StoreState>,
    private globalTranslate: GlobalLanguageService,
    public sharedService: SharedService,
    private elementRef: ElementRef
  ) {}

  ngOnInit() {
    this.store.dispatch(new SetLocallyChangedField(false));
    this.store.dispatch(new SetVpnServerChangedFlag(false));
    this.store.dispatch(new SetVersionChanged(false));
    this.store.dispatch(new ClearCurrentBridgeInterfaceConfiguration());
    this.store.dispatch(new ClearDnat());
    this.store.dispatch(new ResetDynamicDnsConfiguration());
    this.store.dispatch(new ClearCurrentHardwareInterfaceConfiguration());
    this.store.dispatch(new ClearCurrentIpsecInterface());
    this.store.dispatch(new ClearCurrentPrismaInterface());
    this.store.dispatch(new ClearCurrentQoS());
    this.store.dispatch(new ClearRemoteSSH());
    this.store.dispatch(new ClearSplitDns());
    this.store.dispatch(new ClearStaticRouting());
    this.store.dispatch(new ClearStaticRoutingDetail());
    this.store.dispatch(new ClearCurrentDevice());
    this.store.dispatch(new ClearTrafficSteeringStatistics());
    this.store.dispatch(new ClearCurrentTunnelInterfaceConfiguration());
    this.store.dispatch(new ClearCurrentUsbInterfaceConfiguration());
    this.store.dispatch(new ClearCurrentVlanInterfaceConfiguration());
    this.store.dispatch(new ClearVpnRouting());
    this.store.dispatch(new ClearVpnRoutingServer());
    this.store.dispatch(new ClearCurrentWirelessInterfaceConfiguration());
    this.store.dispatch(new ClearStaticHostConfiguration());
    this.store.dispatch(clearIpsecServerAction());
    this.store.dispatch(clearIpsecClientAction());
    this.store.dispatch(clearIpsecCertificateAction());
    this.store.dispatch(clearIpsecStatisticsAction());
    this.store.dispatch(clearActiveHosts());
    this.store.dispatch(clearTrafficStatistics());
    this.store.dispatch(clearUserGroups());
    this.store.dispatch(clearTsForwardSettings());
    this.store.dispatch(new SetActiveNetSubService(''));
    this.store.dispatch(new SetActiveTsSubService(''));
    this.store.dispatch(new SetActiveTsSettingsSubService(''));
    this.store.dispatch(new SetActiveVPNSubService(''));
    this.store.dispatch(new SetActiveSuricataSubService(''));
    this.store.dispatch(clearCellularInterface());
    this.store.dispatch(clearSuricataConfiguration());
    this.store.dispatch(clearBypassList());
    this.store.dispatch(updateLastRefreshedAt({ lastRefreshedAt: null }));
    localStorage.removeItem('tabScrollPosition');
    this.store.dispatch(new ClearDynamicRoutingConfiguration());
    this.store.dispatch(new ClearExistingTunnelConfiguration());
    this.store.dispatch(new SetActiveRoutingSubService(''));
    this.store.dispatch(new SetActiveDynamicRoutingSubService(''));
    this.store.dispatch(clearDynamicRoutingStatisticsAction());
    this.store.dispatch(clearPolicyRoutingStatisticsAction());
    this.store.dispatch(new SetActiveDnsSubService(''));

    this.id = this.route.snapshot.params.id;
    this.isSuperAdmin = this.sharedService.isSuperAdmin;
    if (!this.organization) {
      this.fetchOrganizationDetail();
    }
    this.periodicallyFetch();
    const navHistory = this.navigationService.getHistory();
    this.showBack = navHistory[navHistory.length - 2] !== '/verifyotp';
  }

  ngAfterViewInit(): void {
    this.initMap();
  }

  ngOnDestroy(): void {
    this.elementRef.nativeElement.querySelector('#map')?.remove();
    this.subscriptions.unsubscribe();
    this._intervalService.clearInterval();
  }

  fetchOrganizationDetail() {
    this.getCurrentOrganization();
    this.getCurrentOrganizationWan();
    this.getCurrentOrganizationTunnel();
  }

  periodicallyFetch() {
    this._intervalService.setInterval(FETCH_INTERVAL_DURATION, () =>
      this.fetchOrganizationDetail()
    );
  }

  goBack(): void {
    this.navigationService.back();
  }

  goToEdit(): void {
    this.store.dispatch(new OrganizationsActions.SetEditOrganization(this.organization));
    this.router.navigate(['/organizations', this.id, 'edit']);
  }

  gotoDetailView(params: Device): void {
    const id = params.id;
    this.router.navigate([`/devices`, id]);
  }

  private getCurrentOrganization(): void {
    this.subscriptions.add(
      this.organizationService.getCurrentOrganization(this.id).subscribe({
        next: (organization) => {
          this.organization = organization;
          this.devices = organization.devices;
          this.setDevicesStateWise();
          if (isUndefined(this.currentPlotDeviceMarkersType)) {
            this.plotDeviceMarkers(DeviceListType.deviceList);
          } else {
            this.plotDeviceMarkers(this.currentPlotDeviceMarkersType);
          }

          this.devices = orderBy(organization.devices, ['state'], ['desc']);
          this.isPageLoading = false;
        },
        error: (error) => {
          this.toast.error(error.message);
          this.isPageLoading = false;
        },
      })
    );
  }

  private getCurrentOrganizationWan() {
    const wanUpDevices: any[] = [];
    const wanDownDevices: any[] = [];
    this.subscriptions.add(
      this.organizationService.getCurrentOrganizationWanDetails(this.id).subscribe({
        next: (wanDetails) => {
          wanDetails.forEach((currentWan) => {
            this.lastSynced = new Date();
            if (currentWan.wan_status === INTERFACE_STATUS.UP) {
              wanUpDevices.push(currentWan);
            } else {
              wanDownDevices.push(currentWan);
            }
          });
          this.wanUpDevices = sortBy(wanUpDevices, ['device_name', 'user_defined_name']);
          this.wanDownDevices = sortBy(wanDownDevices, ['device_name', 'user_defined_name']);
        },
        error: (error) => {
          this.toast.error(error.message);
          this.isPageLoading = false;
        },
      })
    );
  }

  private getCurrentOrganizationTunnel(): void {
    const tunnelUpDevices: any[] = [];
    const tunnelDownDevices: any[] = [];
    this.subscriptions.add(
      this.organizationService.getCurrentOrganizationTunnelDetails(this.id).subscribe({
        next: (tunnelDetailsList: TunnelDetails[]) => {
          tunnelDetailsList.forEach((tunnelDetail: TunnelDetails) => {
            this.lastSynced = new Date();
            tunnelDetail.interface_state === INTERFACE_STATUS.UP
              ? tunnelUpDevices.push(tunnelDetail)
              : tunnelDownDevices.push(tunnelDetail);
          });
          this.tunnelUpDevices = sortBy(tunnelUpDevices, ['device_name', 'user_defined_name']);
          this.tunnelDownDevices = sortBy(tunnelDownDevices, ['device_name', 'user_defined_name']);
        },
        error: (error: Error) => {
          this.toast.error(error.message);
          this.isPageLoading = false;
        },
      })
    );
  }

  private setDevicesStateWise(): void {
    const activeDevices: Device[] = [];
    const offlineDevices: Device[] = [];
    const notProvisionedDevices: Device[] = [];
    this.devices.forEach((device: Device) => {
      if (this.isDeviceOnlineAndNotDeleted(device)) {
        activeDevices.push(device);
      } else {
        offlineDevices.push(device);
      }
      if (device.state === DEVICE_STATE.NOT_PROVISIONED) {
        notProvisionedDevices.push(device);
      }
    });
    this.activeDevices = activeDevices;
    this.offlineDevices = offlineDevices;
    this.notProvisionedDevices = notProvisionedDevices;
  }

  private isDeviceOnlineAndNotDeleted(device: Device): boolean {
    return (
      device.is_online &&
      device.state !== DEVICE_STATE.BEING_DELETED &&
      device.state !== DEVICE_STATE.DELETED
    );
  }

  private initMap(): void {
    this.map = L.map('map', {
      center: [20.5937, 78.9629],
      zoom: 4,
    });

    const tiles = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 18,
      minZoom: 3,
      attribution: '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    });

    tiles.addTo(this.map);
  }

  public plotDeviceMarkers(type): void {
    if (this.map) {
      this.currentPlotDeviceMarkersType = type;
      this.activeDeviceList = type;

      if (!this._intervalService.getInterval()) {
        this.periodicallyFetch();
      }

      switch (type) {
        case DeviceListType.deviceList:
          this.devicesList = this.devices;
          this.columnDefs = this.deviceColumnDefs;
          break;
        case DeviceListType.activeDeviceList:
          this.devicesList = this.activeDevices;
          this.columnDefs = this.deviceColumnDefs;
          break;
        case DeviceListType.offlineDeviceList:
          this.devicesList = this.offlineDevices;
          this.columnDefs = this.deviceColumnDefs;
          break;
        case DeviceListType.notProvisionedDeviceList:
          this.devicesList = this.notProvisionedDevices;
          this.columnDefs = this.deviceColumnDefs;
          break;
        case DeviceListType.wanUpDeviceList:
          this.devicesList = this.wanUpDevices;
          this.columnDefs = this.wanColumnDefs;
          break;
        case DeviceListType.wanDownDeviceList:
          this.devicesList = this.wanDownDevices;
          this.columnDefs = this.wanColumnDefs;
          break;
        case DeviceListType.tunnelUpDeviceList:
          this.devicesList = this.tunnelUpDevices;
          this.columnDefs = this.tunnelColumnDefs;
          break;
        case DeviceListType.tunnelDownDeviceList:
          this.devicesList = this.tunnelDownDevices;
          this.columnDefs = this.tunnelColumnDefs;
          break;
      }

      this.markerLayersClusterGroup.clearLayers();
      this.devicesList.forEach((device: any) => {
        const marker = L.marker([device.latitude, device.longitude], {
          icon:
            this.isDeviceOnlineAndNotDeleted(device) || device.runtime_status === 'UP'
              ? this.onlineIcon
              : this.offlineIcon,
        }).on('click', () => {
          if (device.state !== DEVICE_STATE.NOT_PROVISIONED) {
            const deviceId = device.id || device.device_id;
            this.router.navigate(['/devices', deviceId, 'monitoring']);
          }
        });
        marker.bindTooltip(this.getDeviceTooltip(device));
        this.markerLayersClusterGroup.addLayer(marker);
      });

      if (!this.markerClusterClicked) {
        this.markerLayersClusterGroup.on('clusterclick', () => {
          this.markerClusterClicked = true;
          if (this._intervalService.getInterval()) {
            this._intervalService.clearInterval();
          }
        });
      }
      this.map.addLayer(this.markerLayersClusterGroup);
      if (this.initialRendering) {
        this.initialRendering = false;
        const bounds = this.markerLayersClusterGroup.getBounds();
        if (bounds && bounds.isValid()) {
          this.map?.fitBounds(bounds);
        }
      }
      this.sizeToFit();
    }
  }

  public onToggleView(): void {
    this.isTableView = !this.isTableView;
  }

  private getDeviceTooltip(device: Device): L.Tooltip {
    let content = '';
    if (device.name) {
      content = content.concat(
        `<div>${this.localizeHeader('DEVICES.LIST.NAME')}:${device.name}</div>`
      );
    }
    if (device.uuid) {
      content = content.concat(
        `<div>${this.localizeHeader('DEVICES.LIST.UUID')}:${device.uuid}</div>`
      );
    }
    if (device.is_online) {
      content = content.concat(
        `<div>${this.localizeHeader('DEVICES.LIST.WEBSOCKET')}:${device.is_online}</div>`
      );
    }
    if (device.type) {
      content = content.concat(
        `<div>${this.localizeHeader('DEVICES.LIST.TYPE')}:${device.type}</div>`
      );
    }
    if (device.state) {
      content = content.concat(
        `<div>${this.localizeHeader('DEVICES.LIST.STATE')}:${device.state}</div>`
      );
    }
    return L.tooltip().setContent(content);
  }

  public localizeHeader(params: string): string {
    return this.globalTranslate.getTranslation(params);
  }

  public onToggleCollapse(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  sizeToFit() {
    this.gridApi?.sizeColumnsToFit();
  }
}
